import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowRight, Building2, Plus, Sparkles, Trash2, Wand2, Layers, Check,
} from "lucide-react";
import { OperationalShell } from "../components/OperationalShell";
import { AssignmentSummaryPanel } from "../components/AssignmentSummaryPanel";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";

/**
 * SetupWizard — Phase 6 "Guided Provisioning"
 *
 * Calm, mobile-first, multi-step provisioning. Internal step state
 * (no router hops) so the operator can move freely between steps
 * without losing context. Every step carries:
 *   • conversational copy ("What happens next?")
 *   • generous padding + large touch targets
 *   • progressive disclosure (advanced settings hidden)
 *   • reassurance language about local autonomy
 *
 * Steps: type → hierarchy → template → branding → confirm.
 */
const STEPS = ["type", "hierarchy", "template", "branding", "confirm"];

const SetupWizard = () => {
    const { t } = useTranslation();
    const { user, loading: authLoading } = useAuth();
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    // Wizard state.
    const [operationType, setOperationType] = useState("casual_dining");
    const [parents, setParents] = useState([]);
    const [parentOrgId, setParentOrgId] = useState("");
    const [regionName, setRegionName] = useState("");
    const [locations, setLocations] = useState([{ name: "" }]);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [accentColor, setAccentColor] = useState("#10B981");
    const [welcome, setWelcome] = useState("");
    const [contact, setContact] = useState("");
    const [demoBusy, setDemoBusy] = useState(false);
    // v4.1.1 — Post-Provision Recap. Holds the assignment id of the
    // template applied during provisioning (if any) so the user can
    // open the calm forensic panel directly from the success toast.
    const [recapAssignmentId, setRecapAssignmentId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [hres, tres] = await Promise.all([
                    api.get("/operational-base/hierarchy"),
                    api.get("/operational-base/templates"),
                ]);
                if (cancelled) return;
                // Flatten roots so the user can attach beneath any corporate.
                const flat = [];
                const walk = (n, depth) => {
                    flat.push({ id: n.id, name: n.name, depth, org_type: n.org_type });
                    for (const c of (n.children || [])) walk(c, depth + 1);
                };
                for (const r of (hres.data?.hierarchy || [])) walk(r, 0);
                setParents(flat);
                setTemplates(tres.data?.templates || []);
            } catch (_) { /* non-fatal */ }
        })();
        return () => { cancelled = true; };
    }, []);

    if (authLoading) return null;
    if (!user) return <Navigate to="/login" replace />;

    const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
    const back = () => setStep((s) => Math.max(s - 1, 0));

    const canAdvance = () => {
        if (step === 1) return regionName.trim().length > 0;
        return true;
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                operation_type: operationType,
                parent_org_id: parentOrgId || null,
                region: {
                    name: regionName,
                    locations: locations.filter((l) => l.name.trim()),
                },
                apply_template_id: selectedTemplateId || null,
                branding: {
                    accent_color: accentColor || null,
                    welcome_message: welcome || null,
                    contact_email: contact || null,
                },
                is_sandbox: false,
            };
            const res = await api.post("/operational-base/wizard/provision", payload);
            const data = res.data || {};
            // v4.1.1 — Post-Provision Recap chip. If a template was
            // actually applied, surface a calm "See exactly what was
            // applied" action right inside the success toast. Closes
            // the loop from "I think I did it right" → "I know I did
            // it right." If no template was applied (operator chose
            // Skip), the toast stays minimal and we navigate normally.
            if (data.template_assignment_id) {
                toast.success(
                    t("ob.wizard.success_toast_with_recap", { name: data.region_name }),
                    {
                        duration: 12000,
                        action: {
                            label: t("ob.wizard.recap_action"),
                            onClick: () => setRecapAssignmentId(data.template_assignment_id),
                        },
                    },
                );
                // Don't auto-navigate — let the operator open the
                // recap (or dismiss the toast). They can navigate
                // manually when ready.
            } else {
                toast.success(t("ob.wizard.success_toast", { name: data.region_name }));
                nav("/operational-base/templates");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("ob.wizard.error_toast"));
        } finally {
            setSubmitting(false);
        }
    };

    const generateDemo = async () => {
        setDemoBusy(true);
        try {
            const res = await api.post("/operational-base/wizard/demo", {
                seed_label: "Pop-up Demo Group",
                location_count: 5,
            });
            toast.success(t("ob.wizard.demo_success", { count: res.data.location_ids.length }));
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("ob.wizard.demo_error"));
        } finally {
            setDemoBusy(false);
        }
    };

    return (
        <OperationalShell breadcrumb={[{ label: t("ob.wizard.eyebrow") }]}>
            <header className="mb-8 space-y-3" data-testid="ob-wizard-header">
                <p className="ob-eyebrow">{t("ob.wizard.eyebrow")}</p>
                <h1 className="text-4xl sm:text-5xl" style={{ color: "var(--ob-text-primary)" }} data-testid="ob-wizard-title">
                    {t("ob.wizard.title")}
                </h1>
                <p className="text-base max-w-2xl" style={{ color: "var(--ob-text-secondary)" }}>
                    {t("ob.wizard.subtitle")}
                </p>
            </header>

            {/* Progress dots */}
            <div className="flex items-center gap-2 mb-8" data-testid="ob-wizard-progress">
                {STEPS.map((s, i) => (
                    <div
                        key={s}
                        data-testid={`ob-wizard-step-${i}`}
                        data-active={step === i}
                        className="flex items-center gap-2"
                    >
                        <span
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs"
                            style={{
                                background: step >= i ? "rgba(16,185,129,0.20)" : "rgba(255,255,255,0.04)",
                                color: step >= i ? "#A7F3D0" : "var(--ob-text-tertiary)",
                                border: step === i ? "1px solid rgba(16,185,129,0.45)" : "1px solid rgba(255,255,255,0.06)",
                            }}
                        >
                            {step > i ? <Check size={12} strokeWidth={2} aria-hidden="true" /> : i + 1}
                        </span>
                        {i < STEPS.length - 1 && (
                            <span aria-hidden="true" className="w-6 sm:w-10 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
                        )}
                    </div>
                ))}
            </div>

            <div className="ob-card p-6 sm:p-8 max-w-3xl">
                {step === 0 && <StepType operationType={operationType} setOperationType={setOperationType} t={t} />}
                {step === 1 && (
                    <StepHierarchy
                        t={t}
                        parents={parents}
                        parentOrgId={parentOrgId} setParentOrgId={setParentOrgId}
                        regionName={regionName} setRegionName={setRegionName}
                        locations={locations} setLocations={setLocations}
                    />
                )}
                {step === 2 && (
                    <StepTemplate
                        t={t}
                        templates={templates}
                        selectedTemplateId={selectedTemplateId}
                        setSelectedTemplateId={setSelectedTemplateId}
                    />
                )}
                {step === 3 && (
                    <StepBranding
                        t={t}
                        accentColor={accentColor} setAccentColor={setAccentColor}
                        welcome={welcome} setWelcome={setWelcome}
                        contact={contact} setContact={setContact}
                    />
                )}
                {step === 4 && (
                    <StepConfirm
                        t={t}
                        operationType={operationType}
                        parentOrgId={parentOrgId} parents={parents}
                        regionName={regionName} locations={locations}
                        templateId={selectedTemplateId} templates={templates}
                    />
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8 gap-3">
                    <button
                        type="button"
                        onClick={back}
                        disabled={step === 0 || submitting}
                        data-testid="ob-wizard-back"
                        className="inline-flex items-center gap-1.5 text-sm rounded-full px-4 py-2 disabled:opacity-30"
                        style={{ color: "var(--ob-text-secondary)" }}
                    >
                        <ArrowLeft size={14} strokeWidth={1.5} aria-hidden="true" />
                        {t("common.back")}
                    </button>
                    {step < STEPS.length - 1 ? (
                        <button
                            type="button"
                            onClick={next}
                            disabled={!canAdvance()}
                            data-testid="ob-wizard-next"
                            className="inline-flex items-center gap-1.5 text-sm rounded-full px-5 py-2.5"
                            style={{
                                background: canAdvance() ? "#10B981" : "rgba(16,185,129,0.20)",
                                color: canAdvance() ? "#0B0F12" : "var(--ob-text-tertiary)",
                                cursor: canAdvance() ? "pointer" : "not-allowed",
                            }}
                        >
                            {t("common.next")}
                            <ArrowRight size={14} strokeWidth={1.5} aria-hidden="true" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={submit}
                            disabled={submitting}
                            data-testid="ob-wizard-finish"
                            className="inline-flex items-center gap-1.5 text-sm rounded-full px-5 py-2.5"
                            style={{ background: "#10B981", color: "#0B0F12", opacity: submitting ? 0.5 : 1 }}
                        >
                            <Sparkles size={14} strokeWidth={1.5} aria-hidden="true" />
                            {submitting ? t("ob.wizard.creating") : t("ob.wizard.create_btn")}
                        </button>
                    )}
                </div>
            </div>

            {/* Demo generator card */}
            <section className="mt-12 max-w-3xl" data-testid="ob-wizard-demo-section">
                <h2 className="ob-eyebrow mb-3">{t("ob.wizard.demo_section_label")}</h2>
                <div
                    className="ob-card p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                >
                    <div className="min-w-0">
                        <p className="text-base" style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}>
                            {t("ob.wizard.demo_title")}
                        </p>
                        <p className="text-sm mt-1" style={{ color: "var(--ob-text-secondary)" }}>
                            {t("ob.wizard.demo_subtitle")}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={generateDemo}
                        disabled={demoBusy}
                        data-testid="ob-wizard-demo-btn"
                        className="inline-flex items-center gap-1.5 text-sm rounded-full px-4 py-2.5"
                        style={{
                            color: "#A7F3D0",
                            background: "rgba(16,185,129,0.10)",
                            border: "1px solid rgba(16,185,129,0.25)",
                            opacity: demoBusy ? 0.5 : 1,
                        }}
                    >
                        <Wand2 size={14} strokeWidth={1.5} aria-hidden="true" />
                        {demoBusy ? t("ob.wizard.demo_generating") : t("ob.wizard.demo_btn")}
                    </button>
                </div>
            </section>

            {/* v4.1.1 — Post-Provision Recap panel */}
            {recapAssignmentId && (
                <AssignmentSummaryPanel
                    assignmentId={recapAssignmentId}
                    onClose={() => {
                        setRecapAssignmentId(null);
                        nav("/operational-base/templates");
                    }}
                    onRolledBack={() => {
                        setRecapAssignmentId(null);
                        nav("/operational-base/templates");
                    }}
                />
            )}
        </OperationalShell>
    );
};

// ── Steps ──────────────────────────────────────────────────────────

const StepType = ({ operationType, setOperationType, t }) => {
    const types = [
        "quick_service", "casual_dining", "fine_dining",
        "franchise_group", "corporate_pilot", "hospitality_multi_region",
    ];
    return (
        <div data-testid="ob-wizard-step-type">
            <p className="ob-eyebrow">{t("ob.wizard.step_type_label")}</p>
            <h2 className="text-2xl mt-1" style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}>
                {t("ob.wizard.step_type_title")}
            </h2>
            <p className="text-sm mt-2 mb-5" style={{ color: "var(--ob-text-secondary)" }}>
                {t("ob.wizard.step_type_help")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {types.map((opt) => (
                    <button
                        key={opt}
                        type="button"
                        onClick={() => setOperationType(opt)}
                        data-testid={`ob-wizard-type-${opt}`}
                        className="text-left rounded-xl px-4 py-3 transition"
                        style={{
                            background: operationType === opt ? "rgba(16,185,129,0.12)" : "rgba(255,255,255,0.03)",
                            border: operationType === opt ? "1px solid rgba(16,185,129,0.30)" : "1px solid rgba(255,255,255,0.06)",
                            color: operationType === opt ? "var(--ob-text-primary)" : "var(--ob-text-secondary)",
                        }}
                    >
                        <span className="text-sm font-medium">{t(`ob.wizard.type.${opt}`)}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

const StepHierarchy = ({ t, parents, parentOrgId, setParentOrgId, regionName, setRegionName, locations, setLocations }) => {
    const updateLoc = (i, name) => setLocations((l) => l.map((x, idx) => idx === i ? { name } : x));
    const addLoc = () => setLocations((l) => [...l, { name: "" }]);
    const removeLoc = (i) => setLocations((l) => l.filter((_, idx) => idx !== i));
    return (
        <div data-testid="ob-wizard-step-hierarchy">
            <p className="ob-eyebrow">{t("ob.wizard.step_hierarchy_label")}</p>
            <h2 className="text-2xl mt-1" style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}>
                {t("ob.wizard.step_hierarchy_title")}
            </h2>
            <p className="text-sm mt-2 mb-5" style={{ color: "var(--ob-text-secondary)" }}>
                {t("ob.wizard.step_hierarchy_help")}
            </p>
            <div className="space-y-4">
                <div>
                    <label className="ob-eyebrow block mb-1.5">{t("ob.wizard.parent_label")}</label>
                    <select
                        value={parentOrgId}
                        onChange={(e) => setParentOrgId(e.target.value)}
                        data-testid="ob-wizard-parent-select"
                        className="w-full rounded-xl px-3 py-2.5 text-sm"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)" }}
                    >
                        <option value="">{t("ob.wizard.parent_none")}</option>
                        {parents.map((p) => (
                            <option key={p.id} value={p.id}>
                                {"\u00A0".repeat(p.depth * 2)}{p.name} ({p.org_type})
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="ob-eyebrow block mb-1.5">{t("ob.wizard.region_label")}</label>
                    <input
                        type="text"
                        value={regionName}
                        onChange={(e) => setRegionName(e.target.value)}
                        placeholder={t("ob.wizard.region_placeholder")}
                        data-testid="ob-wizard-region-input"
                        className="w-full rounded-xl px-3 py-2.5 text-sm"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)" }}
                    />
                </div>
                <div>
                    <p className="ob-eyebrow mb-1.5">{t("ob.wizard.locations_label")}</p>
                    <div className="space-y-2">
                        {locations.map((loc, i) => (
                            <div key={i} className="flex items-center gap-2" data-testid={`ob-wizard-location-row-${i}`}>
                                <Building2 size={14} strokeWidth={1.5} style={{ color: "var(--ob-text-tertiary)" }} aria-hidden="true" />
                                <input
                                    type="text"
                                    value={loc.name}
                                    onChange={(e) => updateLoc(i, e.target.value)}
                                    placeholder={t("ob.wizard.location_placeholder")}
                                    data-testid={`ob-wizard-location-input-${i}`}
                                    className="flex-1 rounded-xl px-3 py-2 text-sm"
                                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)" }}
                                />
                                {locations.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeLoc(i)}
                                        aria-label={t("ob.wizard.remove_location")}
                                        data-testid={`ob-wizard-location-remove-${i}`}
                                        className="p-1.5 rounded hover:bg-white/5"
                                        style={{ color: "var(--ob-text-tertiary)" }}
                                    >
                                        <Trash2 size={14} strokeWidth={1.5} aria-hidden="true" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={addLoc}
                        data-testid="ob-wizard-location-add"
                        className="mt-2 inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1"
                        style={{ color: "#A7F3D0", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.20)" }}
                    >
                        <Plus size={11} strokeWidth={1.5} aria-hidden="true" />
                        {t("ob.wizard.add_location")}
                    </button>
                </div>
                <p className="text-xs" style={{ color: "var(--ob-text-tertiary)" }}>{t("ob.wizard.hierarchy_reassurance")}</p>
            </div>
        </div>
    );
};

const StepTemplate = ({ t, templates, selectedTemplateId, setSelectedTemplateId }) => {
    return (
        <div data-testid="ob-wizard-step-template">
            <p className="ob-eyebrow">{t("ob.wizard.step_template_label")}</p>
            <h2 className="text-2xl mt-1" style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}>
                {t("ob.wizard.step_template_title")}
            </h2>
            <p className="text-sm mt-2 mb-5" style={{ color: "var(--ob-text-secondary)" }}>
                {t("ob.wizard.step_template_help")}
            </p>
            <div className="space-y-2">
                <button
                    type="button"
                    onClick={() => setSelectedTemplateId("")}
                    data-testid="ob-wizard-template-skip"
                    className="w-full text-left rounded-xl px-4 py-3"
                    style={{
                        background: !selectedTemplateId ? "rgba(16,185,129,0.10)" : "rgba(255,255,255,0.03)",
                        border: !selectedTemplateId ? "1px solid rgba(16,185,129,0.25)" : "1px solid rgba(255,255,255,0.06)",
                    }}
                >
                    <p className="text-sm font-medium" style={{ color: "var(--ob-text-primary)" }}>{t("ob.wizard.template_skip_title")}</p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--ob-text-tertiary)" }}>{t("ob.wizard.template_skip_help")}</p>
                </button>
                {templates.slice(0, 12).map((tmpl) => (
                    <button
                        key={tmpl.id}
                        type="button"
                        onClick={() => setSelectedTemplateId(tmpl.id)}
                        data-testid={`ob-wizard-template-${tmpl.id}`}
                        className="w-full text-left rounded-xl px-4 py-3 inline-flex items-start gap-3"
                        style={{
                            background: selectedTemplateId === tmpl.id ? "rgba(16,185,129,0.10)" : "rgba(255,255,255,0.03)",
                            border: selectedTemplateId === tmpl.id ? "1px solid rgba(16,185,129,0.25)" : "1px solid rgba(255,255,255,0.06)",
                        }}
                    >
                        <Layers size={16} strokeWidth={1.5} style={{ color: "#A7F3D0", marginTop: 2 }} aria-hidden="true" />
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium" style={{ color: "var(--ob-text-primary)" }}>{tmpl.name}</span>
                            <span className="block text-xs mt-0.5" style={{ color: "var(--ob-text-tertiary)" }}>
                                {t("ob.templates.version_label", { v: tmpl.current_version })}
                                {tmpl.description ? ` · ${tmpl.description}` : ""}
                            </span>
                        </span>
                    </button>
                ))}
            </div>
            <p className="text-xs mt-4" style={{ color: "var(--ob-text-tertiary)" }}>{t("ob.wizard.template_reassurance")}</p>
        </div>
    );
};

const StepBranding = ({ t, accentColor, setAccentColor, welcome, setWelcome, contact, setContact }) => (
    <div data-testid="ob-wizard-step-branding">
        <p className="ob-eyebrow">{t("ob.wizard.step_branding_label")}</p>
        <h2 className="text-2xl mt-1" style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}>
            {t("ob.wizard.step_branding_title")}
        </h2>
        <p className="text-sm mt-2 mb-5" style={{ color: "var(--ob-text-secondary)" }}>
            {t("ob.wizard.step_branding_help")}
        </p>
        <div className="space-y-4">
            <div>
                <label className="ob-eyebrow block mb-1.5">{t("ob.wizard.accent_label")}</label>
                <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)}
                       data-testid="ob-wizard-accent-input"
                       className="rounded-xl w-20 h-10" />
            </div>
            <div>
                <label className="ob-eyebrow block mb-1.5">{t("ob.wizard.welcome_label")}</label>
                <input type="text" value={welcome} onChange={(e) => setWelcome(e.target.value)}
                       placeholder={t("ob.wizard.welcome_placeholder")}
                       data-testid="ob-wizard-welcome-input"
                       className="w-full rounded-xl px-3 py-2.5 text-sm"
                       style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)" }} />
            </div>
            <div>
                <label className="ob-eyebrow block mb-1.5">{t("ob.wizard.contact_label")}</label>
                <input type="email" value={contact} onChange={(e) => setContact(e.target.value)}
                       placeholder={t("ob.wizard.contact_placeholder")}
                       data-testid="ob-wizard-contact-input"
                       className="w-full rounded-xl px-3 py-2.5 text-sm"
                       style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)" }} />
            </div>
        </div>
    </div>
);

const StepConfirm = ({ t, operationType, parentOrgId, parents, regionName, locations, templateId, templates }) => {
    const parent = parents.find((p) => p.id === parentOrgId);
    const tmpl = templates.find((x) => x.id === templateId);
    const filledLocations = locations.filter((l) => l.name.trim());
    return (
        <div data-testid="ob-wizard-step-confirm">
            <p className="ob-eyebrow">{t("ob.wizard.step_confirm_label")}</p>
            <h2 className="text-2xl mt-1" style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}>
                {t("ob.wizard.step_confirm_title")}
            </h2>
            <p className="text-sm mt-2 mb-5" style={{ color: "var(--ob-text-secondary)" }}>
                {t("ob.wizard.step_confirm_help")}
            </p>
            <div
                className="rounded-xl p-4 space-y-2 text-sm"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
                <p style={{ color: "var(--ob-text-secondary)" }}>
                    · {t("ob.wizard.confirm_type", { type: t(`ob.wizard.type.${operationType}`) })}
                </p>
                <p style={{ color: "var(--ob-text-secondary)" }}>
                    · {parent
                        ? t("ob.wizard.confirm_parent", { name: parent.name })
                        : t("ob.wizard.confirm_no_parent")}
                </p>
                <p style={{ color: "var(--ob-text-secondary)" }}>
                    · {t("ob.wizard.confirm_region", { name: regionName || "—", count: filledLocations.length })}
                </p>
                {tmpl && (
                    <p style={{ color: "#A7F3D0" }}>
                        · {t("ob.wizard.confirm_template", { name: tmpl.name })}
                    </p>
                )}
                {!tmpl && (
                    <p style={{ color: "var(--ob-text-tertiary)" }}>
                        · {t("ob.wizard.confirm_no_template")}
                    </p>
                )}
            </div>
            <p className="text-xs mt-3" style={{ color: "var(--ob-text-tertiary)" }}>{t("ob.wizard.confirm_reassurance")}</p>
        </div>
    );
};

export default SetupWizard;
